/**
 * Репозиторий для работы с событиями в IndexedDB
 */
import { db, type EventRecord } from './db'
import { v4 as uuidv4 } from 'uuid'

export class EventsRepo {
  /**
   * Сохраняет событие локально.
   * Если alreadySynced=true — запись помечается как уже синхронизированная (synced=1),
   * что предотвращает повторную отправку на сервер (используется для авто-закрытых событий).
   */
  static async save(
    event: Omit<EventRecord, 'id' | 'synced' | 'sync_attempts' | 'sync_failed' | 'created_at'>,
    options?: { alreadySynced?: boolean }
  ): Promise<string> {
    const id = uuidv4()
    const now = new Date().toISOString()
    
    const record: EventRecord = {
      id,
      ...event,
      synced: options?.alreadySynced ? 1 : 0,
      sync_attempts: 0,
      sync_failed: 0,
      created_at: now,
    }
    
    await db.events.add(record)
    return id
  }

  /**
   * Получает несинхронизированные события
   */
  static async getUnsynced(limit: number = 100): Promise<EventRecord[]> {
    // Получаем несинхронизированные события, исключая те что провалились (sync_failed=1)
    return await db.events
      .where('synced')
      .equals(0) // Dexie использует числа для boolean индексов
      .filter((event) => event.sync_failed !== 1) // Исключаем проваленные события
      .limit(limit)
      .toArray()
  }

  /**
   * Помечает событие как синхронизированное
   */
  static async markSynced(id: string): Promise<void> {
    await db.events.update(id, { synced: 1 }) // Dexie использует числа для boolean
  }

  /**
   * Помечает несколько событий как синхронизированные (батчинг)
   */
  static async markSyncedBatch(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    
    // Используем transaction для атомарности
    await db.transaction('rw', db.events, async () => {
      await Promise.all(ids.map(id => db.events.update(id, { synced: 1 })))
    })
  }

  /**
   * Увеличивает счётчик попыток синхронизации
   */
  static async incrementSyncAttempts(id: string): Promise<void> {
    const event = await db.events.get(id)
    if (event) {
      await db.events.update(id, {
        sync_attempts: event.sync_attempts + 1,
        sync_failed: event.sync_attempts >= 5 ? 1 : 0, // После 5 попыток помечаем как failed
      })
    }
  }

  /**
   * Увеличивает счётчик попыток синхронизации для нескольких событий (батчинг)
   */
  static async incrementSyncAttemptsBatch(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    
    await db.transaction('rw', db.events, async () => {
      const events = await db.events.bulkGet(ids)
      const updates = events
        .filter((e): e is EventRecord => e !== undefined)
        .map(event => ({
          key: event.id,
          changes: {
            sync_attempts: event.sync_attempts + 1,
            sync_failed: event.sync_attempts >= 5 ? 1 : 0,
          }
        }))
      
      await Promise.all(updates.map(u => db.events.update(u.key, u.changes)))
    })
  }

  /**
   * Получает все события с фильтрами
   */
  static async getAll(filters?: {
    employee_id?: string
    site_id?: string
    event_type?: 'check_in' | 'check_out'
    limit?: number
  }): Promise<EventRecord[]> {
    let query = db.events.orderBy('happened_at').reverse()
    
    if (filters?.employee_id) {
      query = query.filter((e) => e.employee_id === filters.employee_id)
    }
    
    if (filters?.site_id) {
      query = query.filter((e) => e.site_id === filters.site_id)
    }
    
    if (filters?.event_type) {
      query = query.filter((e) => e.event_type === filters.event_type)
    }
    
    const limit = filters?.limit || 100
    return await query.limit(limit).toArray()
  }

  /**
   * Получает количество несинхронизированных событий
   */
  static async getUnsyncedCount(): Promise<number> {
    // Считаем только несинхронизированные события, исключая проваленные
    const events = await db.events.where('synced').equals(0).toArray()
    return events.filter((event) => event.sync_failed !== 1).length
  }

  /**
   * Получает последнее событие сотрудника на объекте
   */
  static async getLastEvent(
    employee_id: string,
    site_id: string
  ): Promise<EventRecord | null> {
    // Первичный путь: составной индекс [employee_id+site_id+happened_at]
    const events = await db.events
      .where('[employee_id+site_id+happened_at]')
      .between(
        [employee_id, site_id, ''],
        [employee_id, site_id, '\uffff'],
        true,
        true
      )
      .reverse()
      .limit(1)
      .toArray()

    if (events.length > 0) return events[0]

    // Запасной путь: используем индекс employee_id (O(M) где M — события этого сотрудника,
    // обычно <50), а НЕ полное сканирование всей таблицы по happened_at (O(N) где N = все события).
    // Это критично: с 700+ записями в таблице O(N)-скан занимал >5s из-за фрагментации SQLite.
    const empEvents = await db.events
      .where('employee_id')
      .equals(employee_id)
      .filter((e) => e.site_id === site_id)
      .toArray()

    if (empEvents.length === 0) return null

    // Сортируем по happened_at и берём последнее событие
    empEvents.sort((a, b) => (a.happened_at > b.happened_at ? -1 : 1))
    return empEvents[0]
  }

  /**
   * Определяет следующий тип события для сотрудника на объекте
   * Возвращает 'check_in' если последнее событие было 'check_out' или его нет
   * Возвращает 'check_out' если последнее событие было 'check_in'
   */
  static async determineNextEventType(
    employee_id: string,
    site_id: string
  ): Promise<'check_in' | 'check_out'> {
    const lastEvent = await this.getLastEvent(employee_id, site_id)
    
    if (!lastEvent) {
      // Если событий нет, начинаем с check_in
      return 'check_in'
    }

    // Если последнее событие было check_in, следующее должно быть check_out
    if (lastEvent.event_type === 'check_in') {
      return 'check_out'
    }

    // Если последнее событие было check_out, следующее должно быть check_in
    return 'check_in'
  }

  /**
   * Получает события с ошибками синхронизации
   */
  static async getFailed(limit: number = 100): Promise<EventRecord[]> {
    return await db.events
      .where('sync_failed')
      .equals(1)
      .limit(limit)
      .toArray()
  }

  /**
   * Сбрасывает статус failed для повторной попытки синхронизации
   */
  static async resetFailedStatus(id: string): Promise<void> {
    await db.events.update(id, {
      sync_failed: 0,
      sync_attempts: 0,
    })
  }

  /**
   * Удаляет старые синхронизированные события (опционально, для очистки)
   */
  static async deleteOldSynced(daysOld: number = 30): Promise<number> {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysOld)
    const cutoffISO = cutoffDate.toISOString()
    
    const oldEvents = await db.events
      .where('synced')
      .equals(1)
      .filter((e) => e.happened_at < cutoffISO)
      .toArray()
    
    const ids = oldEvents.map((e) => e.id)
    await db.events.bulkDelete(ids)
    
    return ids.length
  }

  /**
   * Удаляет все события для указанного site_id (используется при перерегистрации)
   */
  static async deleteBySiteId(siteId: string): Promise<number> {
    const events = await db.events
      .where('site_id')
      .equals(siteId)
      .toArray()
    
    const ids = events.map((e) => e.id)
    await db.events.bulkDelete(ids)
    
    return ids.length
  }

  /**
   * Удаляет все несинхронизированные события (используется при перерегистрации)
   */
  static async deleteUnsynced(): Promise<number> {
    const events = await db.events
      .where('synced')
      .equals(0)
      .toArray()
    
    const ids = events.map((e) => e.id)
    await db.events.bulkDelete(ids)
    
    return ids.length
  }

  /**
   * Проверяет валидность UUID строки
   */
  static isValidUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(str)
  }
}

